import {
  Ban,
  Check,
  CheckCircle2,
  ExternalLink,
  PencilLine,
  Save,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { BlogPostStatusBadge } from '../../components/ui/data';
import { ConfirmDialog, Drawer, useToast } from '../../components/ui/overlays';
import { Badge, Button, Field, Input, Textarea } from '../../components/ui/primitives';
import {
  useApproveBlogPost,
  useBlogPost,
  useCancelBlogPost,
  useDeleteBlogPost,
  usePublishBlogPost,
  useUpdateBlogPost,
} from '../../hooks/queries';
import { formatDateTime, truncate } from '../../lib/utils';
import type { SeoCheck } from '../../types/api';
import { ScheduleEditModal } from './ScheduleEditModal';

/** Parse the SEO checklist JSON string into typed items, tolerating a null or malformed value. */
const parseSeoChecklist = (raw: string | null): SeoCheck[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as SeoCheck[];
  } catch {
    return [];
  }
};

const BlogPostDrawer = ({
  postId,
  projectId,
  onClose,
}: {
  postId: string | null;
  projectId: string;
  onClose: () => void;
}) => {
  const { data: post } = useBlogPost(postId ?? undefined);
  const updatePost = useUpdateBlogPost(projectId);
  const approvePost = useApproveBlogPost(projectId);
  const publishPost = usePublishBlogPost(projectId);
  const cancelPost = useCancelBlogPost(projectId);
  const deletePost = useDeleteBlogPost(projectId);
  const toast = useToast();

  const [draft, setDraft] = useState({
    title: '',
    metaDescription: '',
    excerpt: '',
    tags: '',
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [scheduleEdit, setScheduleEdit] = useState(false);

  useEffect(() => {
    if (!post) return;
    setDraft({
      title: post.title,
      metaDescription: post.metaDescription ?? '',
      excerpt: post.excerpt ?? '',
      tags: post.tags ?? '',
    });
  }, [post]);

  const checklist = useMemo(() => parseSeoChecklist(post?.seoChecklist ?? null), [post?.seoChecklist]);

  const editable = post ? ['DRAFT', 'SCHEDULED', 'FAILED', 'CANCELLED'].includes(post.status) : false;
  const activeRevision =
    post?.revisions?.find((r) => ['DRAFT', 'SCHEDULED', 'PUBLISHING'].includes(r.status)) ?? null;
  const isRevision = Boolean(post?.revisesPostId);
  const dirty =
    Boolean(post) &&
    (draft.title !== post?.title ||
      draft.metaDescription !== (post?.metaDescription ?? '') ||
      draft.excerpt !== (post?.excerpt ?? '') ||
      draft.tags !== (post?.tags ?? ''));

  const save = async () => {
    if (!post) return;
    try {
      await updatePost.mutateAsync({
        id: post.id,
        title: draft.title,
        metaDescription: draft.metaDescription,
        excerpt: draft.excerpt,
        tags: draft.tags,
      });
      toast.success('Post updated');
    } catch (error) {
      toast.error(error);
    }
  };

  const approve = async () => {
    if (!post) return;
    try {
      await approvePost.mutateAsync(post.id);
      toast.success('Approved and scheduled');
    } catch (error) {
      toast.error(error);
    }
  };

  const publish = async () => {
    if (!post) return;
    try {
      const result = await publishPost.mutateAsync(post.id);
      if (result.status === 'PUBLISHED') {
        toast.success(result.externalUrl ? `Published to ${result.externalUrl}` : 'Published');
      } else if (result.status === 'SKIPPED') {
        toast.info('Skipped — the post did not meet the publish rules.');
      } else {
        toast.error(result.error ?? 'Publish failed');
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const cancel = async () => {
    if (!post) return;
    try {
      await cancelPost.mutateAsync(post.id);
      toast.success('Cancelled');
    } catch (error) {
      toast.error(error);
    }
  };

  const acting =
    approvePost.isPending || publishPost.isPending || cancelPost.isPending;

  return (
    <>
      <Drawer
        open={Boolean(postId)}
        onClose={onClose}
        width="lg"
        title={post ? truncate(post.title, 64) : 'Post'}
        subtitle={
          post ? (
            <span className="flex flex-wrap items-center gap-2">
              <BlogPostStatusBadge status={post.status} />
              {post.channel ? <Badge tone="brand">{post.channel.label}</Badge> : null}
              {post.seoScore !== null ? <Badge tone="neutral">SEO {post.seoScore}</Badge> : null}
              {post.topic ? (
                <span className="text-2xs text-faint">{truncate(post.topic.title, 40)}</span>
              ) : null}
            </span>
          ) : null
        }
        footer={
          post ? (
            <>
              <Button
                variant="ghost"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setConfirmDelete(true)}
                disabled={post.status === 'PUBLISHED'}
              >
                Delete
              </Button>
              <div className="flex-1" />
              {editable && dirty ? (
                <Button icon={<Save className="h-4 w-4" />} loading={updatePost.isPending} onClick={save}>
                  Save edits
                </Button>
              ) : null}
              {post.status === 'PUBLISHED' && !isRevision ? (
                <Button
                  variant="primary"
                  icon={<PencilLine className="h-4 w-4" />}
                  onClick={() => setScheduleEdit(true)}
                  disabled={Boolean(activeRevision)}
                >
                  {activeRevision ? 'Edit in progress' : 'Schedule edit'}
                </Button>
              ) : null}
              {post.status === 'DRAFT' ? (
                <Button
                  variant="primary"
                  icon={<Check className="h-4 w-4" />}
                  loading={acting}
                  onClick={approve}
                >
                  Approve
                </Button>
              ) : null}
              {editable ? (
                <Button
                  variant="primary"
                  icon={<Send className="h-4 w-4" />}
                  loading={acting}
                  onClick={publish}
                >
                  {isRevision ? 'Update live post' : 'Publish now'}
                </Button>
              ) : null}
              {post.status === 'SCHEDULED' ? (
                <Button icon={<Ban className="h-4 w-4" />} loading={acting} onClick={cancel}>
                  Cancel
                </Button>
              ) : null}
            </>
          ) : null
        }
      >
        {!post ? (
          <div className="p-5 text-[13px] text-faint">Loading…</div>
        ) : (
          <div className="divide-y divide-line">
            {isRevision && post.externalUrl ? (
              <div className="px-5 py-3 text-[13px] text-muted">
                Editing a published post. Approving updates the live post at{' '}
                <a href={post.externalUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                  {post.externalUrl}
                </a>{' '}
                in place.
              </div>
            ) : null}
            {post.status === 'PUBLISHED' && activeRevision ? (
              <div className="px-5 py-3 text-[13px] text-warning">An edit of this post is in progress.</div>
            ) : null}
            {post.status === 'SUPERSEDED' ? (
              <div className="px-5 py-3 text-[13px] text-faint">
                This version was superseded by a later edit.
              </div>
            ) : null}
            <div className="space-y-4 px-5 py-4">
              <Field label="Title" htmlFor="post-title">
                <Input
                  id="post-title"
                  value={draft.title}
                  disabled={!editable}
                  onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                />
              </Field>
              <Field
                label="Meta description"
                htmlFor="post-meta"
                hint="Shown in search results and by AI answer engines. Aim for 150–160 characters."
              >
                <Textarea
                  id="post-meta"
                  rows={2}
                  value={draft.metaDescription}
                  disabled={!editable}
                  onChange={(event) => setDraft({ ...draft, metaDescription: event.target.value })}
                />
              </Field>
              <Field label="Excerpt" htmlFor="post-excerpt">
                <Textarea
                  id="post-excerpt"
                  rows={2}
                  value={draft.excerpt}
                  disabled={!editable}
                  onChange={(event) => setDraft({ ...draft, excerpt: event.target.value })}
                />
              </Field>
              <Field label="Tags" htmlFor="post-tags" hint="Comma separated.">
                <Input
                  id="post-tags"
                  value={draft.tags}
                  disabled={!editable}
                  onChange={(event) => setDraft({ ...draft, tags: event.target.value })}
                />
              </Field>
            </div>

            <div className="px-5 py-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="field-label mb-0">SEO / GEO score</span>
                {post.seoScore !== null ? (
                  <Badge tone={post.seoScore >= 70 ? 'positive' : post.seoScore >= 40 ? 'warning' : 'danger'}>
                    {post.seoScore} / 100
                  </Badge>
                ) : (
                  <span className="text-2xs text-faint">not scored yet</span>
                )}
              </div>
              {checklist.length ? (
                <div className="space-y-1.5 rounded-lg border border-line bg-raised px-3 py-2.5 text-xs">
                  {checklist.map((check) => (
                    <div key={check.id} className="flex items-start gap-2">
                      {check.ok ? (
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-positive" />
                      ) : (
                        <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" />
                      )}
                      <span className="min-w-0">
                        <span className="font-medium text-ink">{check.label}</span>
                        {check.detail ? <span className="ml-1 text-muted">— {check.detail}</span> : null}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[13px] text-faint">No checklist yet — the agent scores the post as it drafts.</p>
              )}
            </div>

            {post.agentNotes ? (
              <div className="px-5 py-4">
                <span className="field-label">Agent notes</span>
                <p className="rounded-lg bg-raised px-3 py-2.5 text-[13px] leading-relaxed text-ink">
                  {post.agentNotes}
                </p>
              </div>
            ) : null}

            <div className="px-5 py-4">
              <span className="field-label">Details</span>
              <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
                {[
                  ['Slug', post.slug ?? '—'],
                  ['Language', post.language ?? '—'],
                  ['Channel', post.channel?.label ?? '—'],
                  ['Created', formatDateTime(post.createdAt)],
                  ['Scheduled', formatDateTime(post.scheduledAt)],
                  ['Approved', formatDateTime(post.approvedAt)],
                  ['Published', formatDateTime(post.publishedAt)],
                  ['Batch', post.batch?.name ?? '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-2">
                    <dt className="shrink-0 text-faint">{label}</dt>
                    <dd className="truncate text-right text-ink">{value}</dd>
                  </div>
                ))}
              </dl>
              {post.externalUrl ? (
                <a
                  href={post.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-[13px] text-brand hover:underline"
                >
                  View published post <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
            </div>

            <div className="px-5 py-4">
              <span className="field-label">Preview</span>
              <div
                className="prose-preview rounded-lg border border-line bg-white p-4 text-[13px] text-slate-900"
                // The HTML here is assembled by our own renderer on the backend, so there is no
                // third-party markup to worry about.
                dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
              />
            </div>
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete post"
        destructive
        confirmLabel="Delete"
        loading={deletePost.isPending}
        message="Delete this post? Only unpublished posts can be removed."
        onConfirm={async () => {
          if (!post) return;
          try {
            await deletePost.mutateAsync(post.id);
            toast.success('Post deleted');
            setConfirmDelete(false);
            onClose();
          } catch (error) {
            toast.error(error);
          }
        }}
      />

      {post ? (
        <ScheduleEditModal
          open={scheduleEdit}
          onClose={() => setScheduleEdit(false)}
          projectId={projectId}
          postId={post.id}
          postTitle={post.title}
        />
      ) : null}
    </>
  );
};

export { BlogPostDrawer };
